import { ApolloLink } from "@apollo/client/core";
import { tap } from "rxjs/operators";

// The status the backend answers with while it is in maintenance.
export const MAINTENANCE_HTTP_STATUS = 503;

/**
 * Reports, after every GraphQL operation, whether the server is in maintenance.
 *
 * The decision relies on the HTTP status of the response (the HttpLink stores it in the operation
 * context) rather than on the error type, so that a 503 is recognized whatever its body is (JSON,
 * HTML page from a proxy, or empty).
 * - 503: the server is in maintenance.
 * - any other status: the server answered, so it is not in maintenance.
 * - no response at all (offline, timeout): nothing is reported, the server state is unknown.
 *
 * Place it before the RetryLink so that only the outcome of the last attempt is reported.
 */
export const createMaintenanceLink = (onServerStatus: (isInMaintenance: boolean) => void) =>
    new ApolloLink((operation, forward) => {
        const report = () => {
            const { response } = operation.getContext() as { response?: Response };
            if (typeof response?.status === "number") {
                onServerStatus(response.status === MAINTENANCE_HTTP_STATUS);
            }
        };

        return forward(operation).pipe(tap({ next: report, error: report }));
    });
